/** @format */

import {
	Avatar,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Pagination,
	Stack,
	Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import Label from "../../label/Label";
import { InfoTile } from "./InvitationCard";
import { colors } from "../../../utility/types/constant";
import { RoleNames } from "../../../utility/types/rolesType";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import {
	submitApproveCircleInvitation,
	submitFetchInvitationReviewQueue,
	submitRejectCircleInvitation,
} from "../../../redux/services/circleInvitationService";
import { useToast } from "../../../providers/ToastProvider";
import RejectInvitationDialog from "./RejectInvitationDialog";

const FONT = "'Montserrat', sans-serif";
const PAGE_SIZE = 5;
const DAY_MS = 24 * 60 * 60 * 1000;
const ALL_CIRCLES = "all";
const MY_CIRCLES = "mine";

interface ReviewInvitation {
	_id: string;
	circleId: string;
	status: string;
	respondedAt: string | null;
	expiresAt: string;
	created_At: string;
	circle: {
		_id: string;
		circleName: string;
		circleImage: string;
		memberCount: number;
	} | null;
	inviterName: string;
	invitee: {
		firstName: string;
		lastName: string;
		email: string;
		profileImage: string;
	};
}

const fadeUp = keyframes`
	0% { opacity: 0; transform: translateY(12px); }
	100% { opacity: 1; transform: translateY(0); }
`;

const getErrorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message || error?.message || fallback;

const formatDate = (value?: string | null) => {
	if (!value) return "—";
	const date = new Date(value);
	if (isNaN(date.getTime())) return "—";
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};

const formatRelativeDays = (value?: string | null) => {
	if (!value) return "";
	const days = Math.floor((Date.now() - new Date(value).getTime()) / DAY_MS);
	if (isNaN(days)) return "";
	if (days <= 0) return "today";
	return `${days} day${days === 1 ? "" : "s"} ago`;
};

const describeExpiry = (expiresAt: string) => {
	const msLeft = new Date(expiresAt).getTime() - Date.now();
	if (isNaN(msLeft)) return { text: "", color: "text.secondary" };
	if (msLeft <= DAY_MS) return { text: "Expires today", color: colors.orangeWarning };
	const days = Math.ceil(msLeft / DAY_MS);
	return {
		text: `in ${days} days`,
		color: days <= 2 ? colors.orangeWarning : "text.secondary",
	};
};

const inviteeName = (invitation: ReviewInvitation) =>
	`${invitation.invitee.firstName || ""} ${invitation.invitee.lastName || ""}`.trim() ||
	invitation.invitee.email;

const ReviewCard = ({
	invitation,
	index,
	isApproving,
	isRejecting,
	isBusy,
	canReject,
	onApprove,
	onReject,
}: {
	invitation: ReviewInvitation;
	index: number;
	isApproving: boolean;
	isRejecting: boolean;
	isBusy: boolean;
	canReject: boolean;
	onApprove: (invitation: ReviewInvitation) => void;
	onReject: (invitation: ReviewInvitation) => void;
}) => {
	const name = inviteeName(invitation);
	const circleName = invitation.circle?.circleName || "Unknown Circle";
	const expiry = describeExpiry(invitation.expiresAt);

	return (
		<Box
			component="article"
			aria-label={`${name} is waiting to join ${circleName}`}
			sx={{
				position: "relative",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				borderRadius: "20px",
				backgroundColor: "#fff",
				border: "1px solid rgba(20, 58, 162, 0.22)",
				boxShadow: "0 12px 32px rgba(20, 58, 162, 0.08)",
				animation: `${fadeUp} 0.45s ease-out both`,
				animationDelay: `${Math.min(index, 8) * 60}ms`,
				"&::before": {
					content: '""',
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					height: 4,
					background: `linear-gradient(90deg, ${colors.blueRibbon}, ${colors.mainSecondary})`,
				},
				"@media (prefers-reduced-motion: reduce)": { animation: "none" },
			}}>
			<Box sx={{ p: { xs: 2, sm: 2.5 }, pb: 2, flex: 1 }}>
				{/* Invitee */}
				<Stack direction="row" spacing={1.75} alignItems="center">
					<Avatar
						alt={name}
						src={invitation.invitee.profileImage || undefined}
						sx={{
							width: 52,
							height: 52,
							fontFamily: FONT,
							fontWeight: 700,
							bgcolor: "rgba(20, 58, 162, 0.10)",
							color: colors.blueRibbon,
						}}>
						{name.charAt(0).toUpperCase()}
					</Avatar>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							component="h3"
							noWrap
							sx={{
								fontFamily: FONT,
								fontSize: "1.02rem",
								fontWeight: 700,
								color: "text.primary",
							}}>
							{name}
						</Typography>
						<Typography
							noWrap
							sx={{ fontFamily: FONT, fontSize: "0.84rem", color: "text.secondary" }}>
							{invitation.invitee.email}
						</Typography>
					</Box>
					<Label color="info" sx={{ display: { xs: "none", sm: "inline-flex" } }}>
						Awaiting approval
					</Label>
				</Stack>

				{/* Circle */}
				<Stack
					direction="row"
					spacing={1.25}
					alignItems="center"
					sx={{
						mt: 2,
						p: 1.25,
						borderRadius: "12px",
						border: "1px dashed rgba(15, 23, 42, 0.12)",
					}}>
					<Avatar
						variant="rounded"
						alt={circleName}
						src={invitation.circle?.circleImage || undefined}
						sx={{
							width: 34,
							height: 34,
							borderRadius: "10px",
							fontSize: "0.85rem",
							fontWeight: 700,
							bgcolor: "rgba(2, 48, 32, 0.08)",
							color: colors.secondPrimary,
						}}>
						{circleName.charAt(0).toUpperCase()}
					</Avatar>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							sx={{
								fontFamily: FONT,
								fontSize: "0.68rem",
								fontWeight: 600,
								letterSpacing: "0.04em",
								textTransform: "uppercase",
								color: "text.secondary",
							}}>
							Wants to join
						</Typography>
						<Typography
							noWrap
							sx={{ fontFamily: FONT, fontSize: "0.92rem", fontWeight: 700 }}>
							{circleName}
						</Typography>
					</Box>
					<Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary" }}>
						<GroupsRoundedIcon sx={{ fontSize: 18 }} />
						<Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", fontWeight: 600 }}>
							{invitation.circle?.memberCount ?? 0}
						</Typography>
					</Stack>
				</Stack>

				{/* Details */}
				<Box
					sx={{
						mt: 1.5,
						display: "grid",
						gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
						gap: 1,
					}}>
					<Box sx={{ gridColumn: "1 / -1" }}>
						<InfoTile
							icon={<PersonRoundedIcon />}
							label="Invited by"
							value={invitation.inviterName}
						/>
					</Box>
					<InfoTile
						icon={<HowToRegRoundedIcon />}
						label="Accepted"
						value={formatDate(invitation.respondedAt)}
						hint={formatRelativeDays(invitation.respondedAt)}
					/>
					<InfoTile
						icon={<EventRoundedIcon />}
						label="Expires"
						value={formatDate(invitation.expiresAt)}
						hint={expiry.text}
						hintColor={expiry.color}
					/>
				</Box>
			</Box>

			<Stack
				direction="row"
				spacing={1.25}
				sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 } }}>
				{canReject && (
					<Button
						fullWidth
						variant="outlined"
						disabled={isBusy}
						onClick={() => onReject(invitation)}
						startIcon={isRejecting ? undefined : <CloseRoundedIcon />}
						aria-label={`Reject ${name}'s invitation to ${circleName}`}
						sx={{
							minHeight: 46,
							borderRadius: "12px",
							textTransform: "none",
							fontFamily: FONT,
							fontWeight: 700,
							color: colors.persianRed,
							borderColor: "rgba(202, 52, 51, 0.45)",
							"&:hover": {
								borderColor: colors.persianRed,
								backgroundColor: "rgba(202, 52, 51, 0.06)",
							},
						}}>
						{isRejecting ?
							<CircularProgress size={20} sx={{ color: colors.persianRed }} />
						:	"Reject"}
					</Button>
				)}
				<Button
					fullWidth
					variant="contained"
					disableElevation
					disabled={isBusy}
					onClick={() => onApprove(invitation)}
					startIcon={isApproving ? undefined : <HowToRegRoundedIcon />}
					aria-label={`Approve ${name} to join ${circleName}`}
					sx={{
						minHeight: 46,
						borderRadius: "12px",
						textTransform: "none",
						fontFamily: FONT,
						fontWeight: 700,
						color: "#fff",
						backgroundColor: colors.secondPrimary,
						"&:hover": { backgroundColor: "#03462f" },
					}}>
					{isApproving ?
						<CircularProgress size={20} sx={{ color: "#fff" }} />
					: canReject ?
						"Approve"
					:	"Approve & add to circle"}
				</Button>
			</Stack>
		</Box>
	);
};

/**
 * Leader/admin review queue for member-sent circle invitations.
 * Admins see every circle; circle leaders see the circles they lead.
 */
const InvitationApprovals = () => {
	const toast = useToast();
	const currentUser = useSelector(selectCurrentUser);
	const isAdmin = Boolean(
		currentUser?.roles?.some(
			(r: any) =>
				r.roleName === RoleNames.ADMIN || r.roleName === RoleNames.SUPER_ADMIN,
		),
	);

	// A user can be an admin and a circle leader at the same time; admins get
	// every circle, plus a quick filter for the circles they lead themselves.
	const ledCircleIds = useMemo(
		() =>
			new Set(
				((currentUser?.unionLeaderOf as any[]) ?? [])
					.map((c: any) => (typeof c === "string" ? c : c?._id))
					.filter(Boolean)
					.map(String),
			),
		[currentUser],
	);

	const [openNav, setOpenNav] = useState(false);
	const [invitations, setInvitations] = useState<ReviewInvitation[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState("");
	const [circleFilter, setCircleFilter] = useState<string>(ALL_CIRCLES);
	const [page, setPage] = useState(1);
	const [busyAction, setBusyAction] = useState<{
		id: string;
		action: "approve" | "reject";
	} | null>(null);
	const [rejectTarget, setRejectTarget] = useState<ReviewInvitation | null>(null);
	const [isRejectOpen, setIsRejectOpen] = useState(false);
	const [confirmTarget, setConfirmTarget] = useState<ReviewInvitation | null>(null);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const listTopRef = useRef<HTMLDivElement>(null);
	const isMountedRef = useRef(true);

	const loadQueue = useCallback(async () => {
		setIsLoading(true);
		setLoadError("");
		try {
			const data = await submitFetchInvitationReviewQueue();
			if (isMountedRef.current) setInvitations(Array.isArray(data) ? data : []);
		} catch (error: any) {
			if (isMountedRef.current) {
				setLoadError(getErrorMessage(error, "Failed to load invitations."));
			}
		} finally {
			if (isMountedRef.current) setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		isMountedRef.current = true;
		loadQueue();
		return () => {
			isMountedRef.current = false;
		};
	}, [loadQueue]);

	// Circle filter chips (only useful when the queue spans several circles)
	const circleOptions = useMemo(() => {
		const counts = new Map<string, { name: string; count: number }>();
		invitations.forEach((inv) => {
			const id = String(inv.circleId);
			const existing = counts.get(id);
			counts.set(id, {
				name: inv.circle?.circleName || "Unknown Circle",
				count: (existing?.count ?? 0) + 1,
			});
		});
		return Array.from(counts.entries())
			.map(([id, value]) => ({ id, ...value }))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [invitations]);

	const myCirclesCount = useMemo(
		() => invitations.filter((inv) => ledCircleIds.has(String(inv.circleId))).length,
		[invitations, ledCircleIds],
	);
	const showMyCirclesFilter = isAdmin && ledCircleIds.size > 0;

	const activeFilter =
		circleFilter === ALL_CIRCLES ||
		(circleFilter === MY_CIRCLES && showMyCirclesFilter) ||
		circleOptions.some((c) => c.id === circleFilter) ?
			circleFilter
		:	ALL_CIRCLES;

	const filteredInvitations = useMemo(() => {
		if (activeFilter === ALL_CIRCLES) return invitations;
		if (activeFilter === MY_CIRCLES) {
			return invitations.filter((inv) => ledCircleIds.has(String(inv.circleId)));
		}
		return invitations.filter((inv) => String(inv.circleId) === activeFilter);
	}, [invitations, activeFilter, ledCircleIds]);

	const pageCount = Math.max(1, Math.ceil(filteredInvitations.length / PAGE_SIZE));
	const currentPage = Math.min(page, pageCount);
	const pageStart = (currentPage - 1) * PAGE_SIZE;
	const shownInvitations = filteredInvitations.slice(pageStart, pageStart + PAGE_SIZE);

	useEffect(() => {
		if (page > pageCount) setPage(pageCount);
	}, [page, pageCount]);

	const handleFilterChange = (value: string) => {
		setCircleFilter(value);
		setPage(1);
	};

	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
		listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const handleRequestApprove = (invitation: ReviewInvitation) => {
		if (busyAction) return;
		setConfirmTarget(invitation);
		setIsConfirmOpen(true);
	};

	const handleConfirmApprove = async () => {
		const target = confirmTarget;
		setIsConfirmOpen(false);
		if (!target) return;

		setBusyAction({ id: target._id, action: "approve" });
		try {
			const result = await submitApproveCircleInvitation(target._id);
			toast.success(
				result?.message ||
					`${inviteeName(target)} was added to ${target.circle?.circleName || "the circle"}.`,
			);
			if (isMountedRef.current) {
				setInvitations((prev) => prev.filter((inv) => inv._id !== target._id));
			}
		} catch (error: any) {
			toast.error(getErrorMessage(error, "Failed to approve the invitation."), {
				title: "Couldn't approve",
			});
			// The invitation may have expired or been handled elsewhere; resync.
			const status = error?.response?.status;
			if (status === 409 || status === 410) loadQueue();
		} finally {
			if (isMountedRef.current) setBusyAction(null);
		}
	};

	const handleRequestReject = (invitation: ReviewInvitation) => {
		if (busyAction || !isAdmin) return;
		setRejectTarget(invitation);
		setIsRejectOpen(true);
	};

	const handleConfirmReject = async (reason: string) => {
		const target = rejectTarget;
		setIsRejectOpen(false);
		if (!target) return;

		setBusyAction({ id: target._id, action: "reject" });
		try {
			const result = await submitRejectCircleInvitation(target._id, reason || undefined);
			toast.success(result?.message || "Invitation rejected.");
			if (isMountedRef.current) {
				setInvitations((prev) => prev.filter((inv) => inv._id !== target._id));
			}
		} catch (error: any) {
			toast.error(getErrorMessage(error, "Failed to reject the invitation."), {
				title: "Couldn't reject",
			});
			const status = error?.response?.status;
			if (status === 409 || status === 410) loadQueue();
		} finally {
			if (isMountedRef.current) setBusyAction(null);
		}
	};

	const shell = (content: React.ReactNode) => (
		<>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />
				{content}
			</Box>
		</>
	);

	if (isLoading && invitations.length === 0) {
		return shell(
			<Container sx={{ display: "flex", justifyContent: "center", mt: 12 }}>
				<LoadingSpinner loading={true} size={70} sx={{ position: "absolute" }} />
			</Container>,
		);
	}

	return (
		<>
			{shell(
				<Container
					maxWidth="lg"
					sx={{ mt: { xs: 11, lg: 12 }, pb: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
					{/* Header */}
					<Stack
						direction="row"
						alignItems="flex-start"
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: { xs: 2, sm: 3 } }}>
						<Box>
							<Typography
								component="h1"
								sx={{
									fontFamily: FONT,
									fontSize: { xs: "1.35rem", sm: "1.6rem" },
									fontWeight: 800,
									color: "text.primary",
								}}>
								Invitation Approvals
							</Typography>
							<Typography
								sx={{
									fontFamily: FONT,
									fontSize: { xs: "0.88rem", sm: "0.95rem" },
									color: "text.secondary",
									mt: 0.5,
								}}>
								{isAdmin ?
									"Invitations sent by members, across all circles, that the invitee has accepted. As an admin you can approve or reject any of them."
								:	"Invitations sent by members of circles you lead, that the invitee has accepted."}
							</Typography>
						</Box>
						<Button
							onClick={loadQueue}
							disabled={isLoading}
							startIcon={
								isLoading ? <CircularProgress size={16} /> : <RefreshRoundedIcon />
							}
							sx={{
								flexShrink: 0,
								minHeight: 40,
								borderRadius: "10px",
								textTransform: "none",
								fontFamily: FONT,
								fontWeight: 700,
								color: colors.blueRibbon,
							}}>
							Refresh
						</Button>
					</Stack>

					{loadError && (
						<Box
							role="alert"
							sx={{
								mb: 2,
								p: 1.5,
								borderRadius: "12px",
								color: colors.persianRed,
								backgroundColor: "rgba(202, 52, 51, 0.06)",
								fontFamily: FONT,
								fontSize: "0.88rem",
							}}>
							{loadError}
						</Box>
					)}

					{/* Circle filter */}
					{(circleOptions.length > 1 || showMyCirclesFilter) && (
						<Stack
							ref={listTopRef}
							direction="row"
							spacing={1}
							sx={{
								mb: { xs: 2, sm: 3 },
								overflowX: "auto",
								pb: 0.5,
								scrollMarginTop: { xs: 88, lg: 104 },
								"&::-webkit-scrollbar": { display: "none" },
							}}>
							{[
								{ id: ALL_CIRCLES, name: "All circles", count: invitations.length },
								...(showMyCirclesFilter ?
									[{ id: MY_CIRCLES, name: "Circles I lead", count: myCirclesCount }]
								:	[]),
								...(circleOptions.length > 1 ? circleOptions : []),
							].map(
								(option) => {
									const selected = activeFilter === option.id;
									return (
										<Chip
											key={option.id}
											label={`${option.name} (${option.count})`}
											onClick={() => handleFilterChange(option.id)}
											sx={{
												flexShrink: 0,
												height: 38,
												borderRadius: "10px",
												fontFamily: FONT,
												fontWeight: 700,
												color: selected ? "#fff" : "text.secondary",
												backgroundColor:
													selected ? colors.secondPrimary : "rgba(15, 23, 42, 0.05)",
												"&:hover": {
													backgroundColor:
														selected ? "#03462f" : "rgba(15, 23, 42, 0.09)",
												},
											}}
										/>
									);
								},
							)}
						</Stack>
					)}

					{/* Queue */}
					{shownInvitations.length > 0 ?
						<Box
							ref={circleOptions.length > 1 || showMyCirclesFilter ? undefined : listTopRef}
							sx={{
								display: "grid",
								gap: { xs: 2, sm: 2.5 },
								scrollMarginTop: { xs: 88, lg: 104 },
								gridTemplateColumns: {
									xs: "1fr",
									md: "repeat(2, minmax(0, 1fr))",
									xl: "repeat(3, minmax(0, 1fr))",
								},
							}}>
							{shownInvitations.map((invitation, index) => (
								<ReviewCard
									key={invitation._id}
									invitation={invitation}
									index={index}
									isApproving={
										busyAction?.id === invitation._id &&
										busyAction.action === "approve"
									}
									isRejecting={
										busyAction?.id === invitation._id &&
										busyAction.action === "reject"
									}
									isBusy={busyAction !== null}
									canReject={isAdmin}
									onApprove={handleRequestApprove}
									onReject={handleRequestReject}
								/>
							))}
						</Box>
					:	<Card
							sx={{
								py: 6,
								px: 3,
								textAlign: "center",
								borderRadius: "20px",
								boxShadow: "none",
								border: "1px dashed rgba(15, 23, 42, 0.15)",
							}}>
							<TaskAltRoundedIcon
								sx={{ fontSize: 44, color: colors.mainSecondary, mb: 1 }}
							/>
							<Typography sx={{ fontFamily: FONT, fontWeight: 700, color: "text.primary" }}>
								You're all caught up
							</Typography>
							<Typography
								sx={{ fontFamily: FONT, fontSize: "0.9rem", color: "text.secondary", mt: 0.5 }}>
								No invitations are waiting for approval right now.
							</Typography>
						</Card>
					}

					{pageCount > 1 && (
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems="center"
							justifyContent="space-between"
							spacing={1.5}
							sx={{ mt: 3 }}>
							<Typography sx={{ fontFamily: FONT, fontSize: "0.85rem", color: "text.secondary" }}>
								Showing {pageStart + 1}–{pageStart + shownInvitations.length} of{" "}
								{filteredInvitations.length}
							</Typography>
							<Pagination
								page={currentPage}
								count={pageCount}
								onChange={handlePageChange}
								shape="rounded"
								siblingCount={0}
								boundaryCount={1}
								sx={{
									"& .MuiPaginationItem-root": {
										minWidth: 40,
										height: 40,
										borderRadius: "10px",
										fontFamily: FONT,
										fontWeight: 700,
									},
									"& .MuiPaginationItem-root.Mui-selected": {
										color: "#fff",
										backgroundColor: colors.secondPrimary,
										"&:hover": { backgroundColor: "#03462f" },
									},
								}}
							/>
						</Stack>
					)}
				</Container>,
			)}

			{/* Approve confirmation — adds a member (and a payout slot if a cycle is scheduled) */}
			<Dialog
				open={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				TransitionProps={{ onExited: () => setConfirmTarget(null) }}
				fullWidth
				maxWidth="xs"
				PaperProps={{ sx: { borderRadius: "20px", m: 2 } }}>
				<DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, pb: 1 }}>
					Add {confirmTarget ? inviteeName(confirmTarget) : "this person"} to the circle?
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontFamily: FONT, color: "text.secondary", lineHeight: 1.6 }}>
						They'll become a member of{" "}
						<strong>{confirmTarget?.circle?.circleName || "this circle"}</strong>. If a
						contribution cycle is scheduled but hasn't started yet, they'll get a payout
						slot and start contributing when it begins.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
					<Button
						onClick={() => setIsConfirmOpen(false)}
						sx={{
							minHeight: 44,
							borderRadius: "12px",
							textTransform: "none",
							fontFamily: FONT,
							fontWeight: 700,
							color: "text.secondary",
						}}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disableElevation
						onClick={handleConfirmApprove}
						sx={{
							minHeight: 44,
							borderRadius: "12px",
							textTransform: "none",
							fontFamily: FONT,
							fontWeight: 700,
							backgroundColor: colors.secondPrimary,
							"&:hover": { backgroundColor: "#03462f" },
						}}>
						Approve
					</Button>
				</DialogActions>
			</Dialog>

			{isAdmin && (
				<RejectInvitationDialog
					open={isRejectOpen}
					inviteeName={rejectTarget ? inviteeName(rejectTarget) : "This person"}
					circleName={rejectTarget?.circle?.circleName || "this circle"}
					onCancel={() => setIsRejectOpen(false)}
					onConfirm={handleConfirmReject}
					onExited={() => setRejectTarget(null)}
				/>
			)}
		</>
	);
};

export default InvitationApprovals;
